import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import { getAuthToken, setAuthToken, clearAuthToken, decodeToken, getAppUrlForUser, setRefreshToken } from '@conext/auth';
import { User, Lock, Loader2, AlertCircle, Eye, EyeOff, Info } from 'lucide-react';
import { debePistaUsuario, loginLang, PISTA_TEXT } from '../utils/loginPista';
import logo from '../assets/LOGO_AZUL_SIN_FONDO.png';

const SUPERADMIN_EMAIL = 'administrador@conextcrm.es';

// El registro self-service multi-vertical (citas, comida, autoescuela,
// marketing) vive aquí mismo, en /signup.
const SIGNUP_URL = process.env.REACT_APP_SIGNUP_URL || '/signup';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  // Pista «tu usuario es el móvil o el email» (22/10/2026): solo por la FORMA de lo tecleado (anti-enumeración).
  const [pista, setPista] = useState(false);
  const pistaText = PISTA_TEXT[loginLang(window.location.search)];

  // Precarga del usuario cuando venimos del signup con "ya tienes cuenta" (?u=...).
  useEffect(() => {
    const u = new URLSearchParams(window.location.search).get('u');
    if (u) setUsername(u);
  }, []);

  // On mount: check if token already exists and is valid → redirect immediately
  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      const payload = decodeToken(token);
      const now = Math.floor(Date.now() / 1000);
      if (payload && payload.exp && payload.exp > now) {
        // Don't reuse tokens that require password change — they may be stale
        if (payload.must_change_password || payload.created_from_vendedor) {
          clearAuthToken();
          setChecking(false);
          return;
        }
        const appUrl = getAppUrlForUser(payload);
        window.location.href = appUrl + '/auth-callback?token=' + token;
        return;
      } else {
        clearAuthToken();
      }
    }
    setChecking(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setPista(false);
    setLoading(true);

    // Los accesos se copian del WhatsApp y se cuela un espacio delante o detrás: el usuario el servidor ya lo
    // limpiaba, la contraseña no («Usuario o contraseña incorrectos» con la buena, Eurovial 30/10/2026).
    const user = username.trim();
    const pass = password.trim();

    if (!user || !pass) {
      setError('El usuario y la contraseña son obligatorios');
      setLoading(false);
      return;
    }

    try {
      let response;
      const normalizedUsername = user.toLowerCase();

      // Detectar modo impersonation: superadmin + formato "password||target@email.com"
      if (normalizedUsername === SUPERADMIN_EMAIL && pass.includes('||')) {
        const separatorIndex = pass.indexOf('||');
        const adminPassword = pass.substring(0, separatorIndex);
        const targetEmail = pass.substring(separatorIndex + 2);

        response = await axiosClient.post('/admin-impersonate', {
          admin_email: normalizedUsername,
          admin_password: adminPassword,
          target_email: targetEmail,
          app: 'login-router',
        });
      } else {
        response = await axiosClient.post('/login', {
          username: user,
          password: pass,
          app: 'login-router',
        });
      }

      if (!response || !response.token) {
        // La lambda devolvió 200 pero sin token (e.g. {error: "..."} o {message: "Unauthorized"})
        const serverMsg = response?.error || response?.message;
        throw new Error(serverMsg || 'Token not found in response');
      }

      const { token } = response;
      setAuthToken(token);
      if (response.refresh_token) {
        setRefreshToken(response.refresh_token);
      }

      const payload = decodeToken(token);
      const appUrl = getAppUrlForUser(payload);
      window.location.href = appUrl + '/auth-callback?token=' + token;
    } catch (err) {
      console.error('Error en el login:', err);
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 429) {
        setError('Demasiados intentos. Espera unos minutos.');
      } else if (data?.error_code === 'QUICK_CODE_EXPIRED') {
        // Código rápido (lN) que ya no sirve porque la cuenta pasó a de pago:
        // mensaje del servidor pidiendo usar las credenciales reales.
        setError(
          data.error ||
            'Este acceso rápido ya no está disponible. Inicia sesión con tu usuario y contraseña.'
        );
      } else {
        setError('Usuario o contraseña incorrectos');
        setPista(debePistaUsuario(username));
      }
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 flex items-center justify-center">
        <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 flex items-center justify-center p-6 relative overflow-y-auto">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-100/40 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-100/40 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '1s' }}
        ></div>
      </div>

      {/* Login card */}
      <div className="relative bg-white rounded-[2.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 p-10 w-full max-w-md transition-all duration-300 hover:shadow-blue-100/30">
        {/* Logo/Branding */}
        <div className="text-center mb-10">
          <img
            src={logo}
            alt="ConextCRM"
            className="w-56 h-auto mx-auto mb-6 drop-shadow-sm"
          />
          <p className="font-bold text-gray-800">Inicia sesión en tu cuenta</p>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-2xl animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 text-red-600 rounded-xl">
                <AlertCircle size={20} />
              </div>
              <p className="text-sm font-bold text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Lo tecleado no es ni un móvil ni un email: se le recuerda qué es su usuario (p. ej. «autoescuela ipunto»). */}
        {pista && (
          <div className="-mt-4 mb-8 p-4 bg-blue-50 border border-blue-100 rounded-2xl" data-testid="login-pista-usuario">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 text-blue-600 rounded-xl shrink-0">
                <Info size={20} />
              </div>
              <div className="text-sm">
                <p className="font-bold text-blue-900">{pistaText.pista}</p>
                <Link to="/forgot-password" className="mt-1 inline-block font-bold text-blue-600 hover:text-blue-700 underline">
                  {pistaText.olvide}
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Login form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Username field */}
          <div>
            <label className="block font-bold uppercase tracking-widest text-[11px] text-gray-400 mb-2.5 ml-1">
              Usuario
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
              </div>
              <input
                type="text"
                name="username"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={username}
                onChange={(e) => { setUsername(e.target.value); setPista(false); }}
                className="block w-full pl-12 pr-4 py-4 bg-white border border-gray-100 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-medium text-gray-700 placeholder:text-gray-300"
                placeholder="Tu móvil o email"
                required
              />
            </div>
          </div>

          {/* Password field */}
          <div>
            <label className="block font-bold uppercase tracking-widest text-[11px] text-gray-400 mb-2.5 ml-1">
              Contraseña
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400 transition-colors group-focus-within:text-blue-500" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full pl-12 pr-12 py-4 bg-white border border-gray-100 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-medium text-gray-700 placeholder:text-gray-300"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                tabIndex={0}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-blue-500 transition-colors"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center px-4 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-bold shadow-lg shadow-blue-200/50 hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin h-5 w-5 mr-2" />
                <span>Iniciando sesión...</span>
              </>
            ) : (
              <span>Iniciar sesión</span>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-10 pt-8 border-t border-gray-50 flex flex-col items-center gap-4">
          <Link
            to="/forgot-password"
            className="text-sm font-bold text-gray-400 hover:text-blue-600 transition-colors"
          >
            ¿Olvidaste tu contraseña?
          </Link>
          <div className="text-sm text-gray-600 font-medium">
            <span>¿Aún no tienes cuenta? </span>
            <a
              href={SIGNUP_URL}
              className="text-blue-600 font-bold hover:text-blue-700 transition-colors"
            >
              Empieza tu prueba gratuita
            </a>
          </div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
            © 2026 ConextCRM
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
